import type { AutocompletePlugin } from "@algolia/autocomplete-js";
import classnames from "classnames";

import style from "./style.module.scss";
import { VoiceButton } from "./VoiceButton/VoiceButton";
import { createVoiceSearch, type VoiceSearchState, VoiceSearchStatus } from "./voiceSearchApi";

// Delay in milliseconds before the voice search is triggered
const VOICE_SEARCH_DELAY = 1000;

export type VoiceSearchDisclaimer = {
    title: string;
    text: string;
};

export const createVoiceSearchPlugin = (
    language: string, // Sets the language of the speech recognition. If not specified, this defaults to the HTML `lang` attribute value, or the user agent's language setting if that isn't set either.
    renderButton: (children: JSX.Element) => void,
    onSelectCallback: (value: string) => void,
    disclaimer: VoiceSearchDisclaimer
): AutocompletePlugin<Record<string, unknown>, undefined> => {
    return {
        subscribe({ setQuery, setIsOpen, refresh }) {
            const isBrowser = typeof document !== "undefined";

            isBrowser &&
                requestAnimationFrame(() => {
                    const input = document.querySelector<HTMLInputElement>(".aa-Input");

                    const voiceSearch = createVoiceSearch({
                        language,
                        onTranscript(transcript: string) {
                            setQuery(transcript);
                            setIsOpen(true);
                            refresh();

                            // Set a timeout so we can see the actual search in the box
                            if (onSelectCallback) {
                                setTimeout(() => {
                                    onSelectCallback(transcript);
                                }, VOICE_SEARCH_DELAY);
                            }
                        },
                        onStateChange(state: VoiceSearchState) {
                            if (
                                state.status === VoiceSearchStatus.WAITING ||
                                state.status === VoiceSearchStatus.ASKING_PERMISSION
                            ) {
                                document && document?.querySelector(".aa-VoiceSearch")?.classList.add("active");
                            } else {
                                if (state.status === VoiceSearchStatus.INITIAL) {
                                    requestAnimationFrame(() => {
                                        input?.focus();
                                    });
                                }
                                document && document?.querySelector(".aa-VoiceSearch")?.classList.remove("active");
                            }
                        },
                    });

                    if (!voiceSearch?.isBrowserSupported()) {
                        return;
                    }
                    renderButton &&
                        renderButton(
                            <div className={classnames(style.buttonWrapper, "aa-VoiceSearch")}>
                                <VoiceButton voiceSearch={voiceSearch} disclaimer={disclaimer} />
                            </div>
                        );
                    window.addEventListener("keydown", (event) => {
                        if (!document.querySelector(".aa-VoiceSearchOverlay") || event.key !== "Escape") {
                            return;
                        }
                        voiceSearch.stop();
                    });
                });
        },
        getSources() {
            return [];
        },
    };
};
